import { useEffect, useId, useMemo, useState } from "react";
import {
  AlertCircle,
  ChevronLeft,
  Image as ImageIcon,
  Languages,
  Loader2,
  Mail,
  Paperclip,
  Send,
  Truck,
  Video,
  X,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { Button } from "@/components/ui/button";
import { isVideo } from "@/components/attachments/constants";
import { useSendSupplierEmail, useSupplierPacket, useTranslateSupplierBody } from "@/hooks/useSuppliers";
import type { SafeSupplier, SupplierSendResult } from "@/api/suppliers";
import { ATTACHMENT_TYPE_LABEL } from "./constants";

/**
 * D-133 C-W2 / C-W3 — «Підготувати звернення постачальнику».
 *
 * 2-STEP (NOT 1-click auto-send):
 *   prepare → choose supplier (auto-default + manual) · subject (auto = number) ·
 *             body: uk safe packet (read-only) for uk suppliers; for non-uk suppliers (D-W2)
 *             original (uk, read-only) + «Перекласти» → editable AI translation (draft) ·
 *             evidence selector (photo/video default-on; oversize files non-selectable, C-W1a)
 *   review  → confirm recipients/attachments/body-language → «Надіслати».
 *
 * D-133 Фаза D — переклад НА ВИМОГУ (manual button), draft перевіряється/правиться ПЕРЕД send
 * (НЕ auto-send). Gateway-fail → status='unavailable' → помилка + ручне редагування активне,
 * флоу НЕ блокується (D-W1a fallback).
 *
 * The Send button is disabled while the request is in flight (C-W1c belt; the backend
 * idempotency key is the authority). smtp_accepted ≠ delivered (C-W1d) — the copy says
 * "прийнято поштовим relay".
 */

function errMsg(e: unknown): string {
  return (e as Error)?.message ?? "Сталася помилка";
}

function mb(bytes: number): string {
  return (bytes / 1048576).toFixed(1);
}

function emailsOf(s: SafeSupplier | null | undefined): string[] {
  if (!s) return [];
  return Array.from(
    new Set(
      [s.email, s.qualityContactEmail, s.salesContactEmail, ...(s.additionalEmails ?? [])]
        .map((e) => (e ?? "").trim())
        .filter((e) => e !== ""),
    ),
  );
}

export function SupplierSendModal({
  complaintId,
  open,
  onClose,
  onSent,
}: {
  complaintId: string;
  open: boolean;
  onClose: () => void;
  onSent: (result: SupplierSendResult) => void;
}) {
  const headingId = useId();
  const packetQ = useSupplierPacket(open ? complaintId : null);
  const packet = packetQ.data;
  const mut = useSendSupplierEmail(complaintId);
  const translateMut = useTranslateSupplierBody(complaintId);

  const [step, setStep] = useState<"prepare" | "review">("prepare");
  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [toSet, setToSet] = useState<Set<string>>(new Set());
  const [ccSet, setCcSet] = useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [freeTextEmail, setFreeTextEmail] = useState("");
  const [freeTextReason, setFreeTextReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  // D-133 Фаза D — переклад тіла (draft). aiDraft = що AI запропонував (audit snapshot);
  // translatedBody = редагований текст у textarea (що людина перевірить і надішле).
  const [aiDraft, setAiDraft] = useState<string | null>(null);
  const [translatedBody, setTranslatedBody] = useState("");
  const [translateError, setTranslateError] = useState<string | null>(null);

  const supplierOptions = useMemo<SafeSupplier[]>(() => {
    if (!packet) return [];
    const byId = new Map<string, SafeSupplier>();
    if (packet.recipients.default) byId.set(packet.recipients.default.id, packet.recipients.default);
    for (const s of packet.recipients.suppliers) byId.set(s.id, s);
    return Array.from(byId.values());
  }, [packet]);

  const selectedSupplier = useMemo(
    () => supplierOptions.find((s) => s.id === supplierId) ?? null,
    [supplierOptions, supplierId],
  );
  const supplierEmails = useMemo(() => emailsOf(selectedSupplier), [selectedSupplier]);

  // D-133 Фаза D — переклад потрібен лише для не-uk постачальника (CSupplier.language).
  const targetLanguage = (selectedSupplier?.language ?? packet?.language ?? "uk").toLowerCase();
  const needsTranslation = targetLanguage !== "uk";

  // init on open / when packet arrives
  useEffect(() => {
    if (!open || !packet) return;
    setStep("prepare");
    setError(null);
    setFreeTextEmail("");
    setFreeTextReason("");
    setSelectedIds(new Set(packet.attachments.filter((a) => a.defaultSelected).map((a) => a.attachmentId)));
    setSupplierId(packet.recipients.default?.id ?? packet.recipients.suppliers[0]?.id ?? null);
  }, [open, packet]);

  // reset recipients + translation draft when the supplier changes (language may differ)
  useEffect(() => {
    const primary = supplierEmails[0] ?? null;
    setToSet(new Set(primary ? [primary] : []));
    setCcSet(new Set());
    setAiDraft(null);
    setTranslatedBody("");
    setTranslateError(null);
  }, [supplierId, supplierEmails.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps

  async function runTranslate() {
    setTranslateError(null);
    try {
      const res = await translateMut.mutateAsync(targetLanguage);
      if (res.status === "ok" && res.translatedBody.trim() !== "") {
        setAiDraft(res.translatedBody);
        setTranslatedBody(res.translatedBody);
      } else {
        // D-W1a — gateway недоступний: дозволяємо ручне редагування, флоу не блокується.
        setAiDraft(null);
        setTranslateError(
          "AI-переклад зараз недоступний. Введіть/відредагуйте текст англійською вручну та надішліть.",
        );
      }
    } catch (e) {
      setAiDraft(null);
      setTranslateError(
        `Не вдалося перекласти (${errMsg(e)}). Введіть текст англійською вручну та надішліть.`,
      );
    }
  }

  if (!open) return null;

  const limits = packet?.limits;
  const selectedBytes = packet
    ? packet.attachments
        .filter((a) => selectedIds.has(a.attachmentId) && a.emailable)
        .reduce((n, a) => n + a.fileSize, 0)
    : 0;
  const overCap = !!limits && selectedBytes > limits.totalCapBytes;

  const freeText = freeTextEmail.trim();
  const usingFreeText = freeText !== "";
  const finalTo = [...toSet, ...(usingFreeText ? [freeText] : [])];
  const finalCc = [...ccSet];

  const reasonOk = !usingFreeText || freeTextReason.trim().length >= 5;
  // D-W2 — для не-uk постачальника не шлемо порожнє тіло (потрібен переклад АБО ручний текст).
  const bodyOk = !needsTranslation || translatedBody.trim() !== "";
  const canProceed =
    !!packet &&
    !!supplierId &&
    finalTo.length > 0 &&
    !overCap &&
    reasonOk &&
    bodyOk &&
    (!usingFreeText || packet.canFreeTextRecipient);

  function toggle(set: Set<string>, key: string): Set<string> {
    const next = new Set(set);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  }

  async function submit() {
    if (!packet || !supplierId) return;
    setError(null);
    try {
      const res = await mut.mutateAsync({
        supplierId,
        recipientsTo: finalTo,
        recipientsCc: finalCc,
        attachmentIds: [...selectedIds],
        freeTextReason: usingFreeText ? freeTextReason.trim() : undefined,
        language: packet.language,
        // D-133 Фаза D — для не-uk постачальника надсилаємо перевірене перекладене тіло;
        // aiDraft (що AI запропонував) йде в audit окремо від finalBody (що людина змінила).
        translatedDraftBody: needsTranslation ? (aiDraft ?? undefined) : undefined,
        finalBody: needsTranslation ? translatedBody.trim() : undefined,
        bodyLanguage: needsTranslation ? "en" : "uk",
      });
      onSent(res);
    } catch (e) {
      setError(errMsg(e));
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      aria-hidden
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-lg border bg-background shadow-2xl"
      >
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h4 id={headingId} className="flex items-center gap-2 text-sm font-semibold">
            <Truck className="h-4 w-4 text-muted-foreground" aria-hidden />
            Звернення постачальнику
            <span className="text-xs font-normal text-muted-foreground">
              · крок {step === "prepare" ? "1" : "2"} з 2
            </span>
          </h4>
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрити"
            className="rounded-md p-1 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          {packetQ.isPending ? (
            <div className="flex h-32 items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : packetQ.isError || !packet ? (
            <p role="alert" className="flex items-center gap-1.5 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" /> {(packetQ.error as Error)?.message ?? "Не вдалося підготувати"}
            </p>
          ) : step === "prepare" ? (
            <>
              {/* supplier */}
              <section className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Постачальник
                </label>
                {supplierOptions.length === 0 ? (
                  <p className="rounded-md border border-warning-foreground/20 bg-warning px-3 py-2 text-[13px] text-warning-foreground">
                    Немає активних постачальників для вибору. Додайте постачальника в довіднику.
                  </p>
                ) : (
                  <select
                    value={supplierId ?? ""}
                    onChange={(e) => setSupplierId(e.target.value || null)}
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  >
                    {supplierOptions.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                        {packet.recipients.default?.id === s.id ? " — типовий (за брендом)" : ""}
                      </option>
                    ))}
                  </select>
                )}
              </section>

              {/* recipients */}
              {selectedSupplier && (
                <section className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Кому
                  </label>
                  {supplierEmails.length === 0 && !packet.canFreeTextRecipient && (
                    <p className="text-[13px] text-destructive">У постачальника немає email-адрес.</p>
                  )}
                  <div className="space-y-1">
                    {supplierEmails.map((em) => (
                      <label key={em} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={toSet.has(em)}
                          onChange={() => {
                            setToSet((s) => toggle(s, em));
                            // a To address must never also sit in CC
                            setCcSet((s) => {
                              const n = new Set(s);
                              n.delete(em);
                              return n;
                            });
                          }}
                        />
                        <Mail className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                        {em}
                      </label>
                    ))}
                  </div>

                  {/* CC — supplier addresses not already in To */}
                  {supplierEmails.some((em) => !toSet.has(em)) && (
                    <div className="space-y-1 pt-1">
                      <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Копія (CC)</span>
                      {supplierEmails
                        .filter((em) => !toSet.has(em))
                        .map((em) => (
                          <label key={em} className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={ccSet.has(em)}
                              onChange={() => setCcSet((s) => toggle(s, em))}
                            />
                            {em}
                          </label>
                        ))}
                    </div>
                  )}

                  {/* free-text — admin/QueueAdmin only (C-W1b) */}
                  {packet.canFreeTextRecipient && (
                    <div className="space-y-1.5 rounded-md border border-dashed px-3 py-2">
                      <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
                        Інша адреса (адмін, з причиною)
                      </span>
                      <input
                        type="email"
                        value={freeTextEmail}
                        onChange={(e) => setFreeTextEmail(e.target.value)}
                        placeholder="external@example.com"
                        className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
                      />
                      {usingFreeText && (
                        <input
                          type="text"
                          value={freeTextReason}
                          onChange={(e) => setFreeTextReason(e.target.value)}
                          placeholder="Причина (мін. 5 символів)"
                          className={cn(
                            "w-full rounded-md border bg-background px-2 py-1.5 text-sm",
                            !reasonOk && "border-destructive",
                          )}
                        />
                      )}
                    </div>
                  )}
                </section>
              )}

              {/* subject (auto = number) */}
              <section className="space-y-1">
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Тема (авто — номер рекламації)
                </label>
                <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm font-medium">{packet.emailSubject}</div>
              </section>

              {/* body — uk safe packet; for non-uk suppliers: original + editable AI translation (D-W2) */}
              <section className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {needsTranslation ? "Оригінал (українською)" : "Текст (безпечний пакет — без даних клієнта)"}
                </label>
                <pre className="max-h-44 overflow-y-auto whitespace-pre-wrap rounded-md border bg-muted/20 px-3 py-2 font-sans text-[13px] text-foreground/90">
                  {packet.body}
                </pre>

                {needsTranslation && (
                  <div className="space-y-1.5 pt-1">
                    <div className="flex items-center justify-between gap-2">
                      <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Переклад ({targetLanguage.toUpperCase()}) — перевірте перед надсиланням
                      </label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={runTranslate}
                        disabled={translateMut.isPending}
                        className="gap-1.5"
                      >
                        {translateMut.isPending ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                        ) : (
                          <Languages className="h-3.5 w-3.5" aria-hidden />
                        )}
                        {aiDraft ? "Перекласти ще раз" : "Перекласти"}
                      </Button>
                    </div>
                    <textarea
                      value={translatedBody}
                      onChange={(e) => setTranslatedBody(e.target.value)}
                      rows={8}
                      placeholder="Натисніть «Перекласти» або введіть текст англійською вручну."
                      className="w-full resize-y rounded-md border bg-background px-3 py-2 font-sans text-[13px] text-foreground/90"
                    />
                    {translateError && (
                      <p className="flex items-start gap-1.5 text-[12px] text-warning-foreground">
                        <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden /> {translateError}
                      </p>
                    )}
                    {aiDraft && !translateError && (
                      <p className="text-[11px] text-muted-foreground">
                        AI-чернетка перекладу. Відредагуйте за потреби — надсилається саме цей текст.
                      </p>
                    )}
                  </div>
                )}
              </section>

              {/* attachment selector (C-W2) */}
              <section className="space-y-1.5">
                <label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <Paperclip className="h-3.5 w-3.5" aria-hidden /> Виберіть докази
                </label>
                {packet.attachments.length === 0 ? (
                  <p className="text-[13px] text-muted-foreground">Немає фото/відео-доказів для надсилання.</p>
                ) : (
                  <div className="space-y-1">
                    {packet.attachments.map((a) => {
                      const RowIcon = isVideo(a.fileMimeType) ? Video : ImageIcon;
                      return (
                        <label
                          key={a.attachmentId}
                          className={cn(
                            "flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-sm",
                            !a.emailable && "opacity-60",
                          )}
                        >
                          <input
                            type="checkbox"
                            disabled={!a.emailable}
                            checked={selectedIds.has(a.attachmentId)}
                            onChange={() => setSelectedIds((s) => toggle(s, a.attachmentId))}
                          />
                          <RowIcon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                          <span className="min-w-0 flex-1 truncate">{a.fileName}</span>
                          <span className="shrink-0 text-[11px] text-muted-foreground">
                            {ATTACHMENT_TYPE_LABEL[a.attachmentType as keyof typeof ATTACHMENT_TYPE_LABEL] ??
                              a.attachmentType}{" "}
                            · {mb(a.fileSize)} МБ
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
                {/* oversize warning (C-W1a) — only for files actually over the per-file cap */}
                {!!limits && packet.attachments.some((a) => a.fileSize > limits.individualCapBytes) && (
                  <p className="text-[12px] text-warning-foreground">
                    Деякі файли завеликі для email (&gt;{limits.individualCapMb} МБ) і не будуть прикріплені —
                    передайте їх іншим погодженим каналом.
                  </p>
                )}
                {limits && (
                  <p className={cn("text-[12px]", overCap ? "text-destructive" : "text-muted-foreground")}>
                    Розмір вкладень: {mb(selectedBytes)} / {limits.totalCapMb} МБ
                    {overCap && " — перевищено ліміт листа, зменшіть вибір"}
                  </p>
                )}
              </section>
            </>
          ) : (
            /* ── review step ── */
            <div className="space-y-3 text-sm">
              <div className="rounded-md border bg-muted/20 px-3 py-2">
                <Row label="Постачальник" value={selectedSupplier?.name ?? "—"} />
                <Row label="Кому" value={finalTo.join(", ") || "—"} />
                {finalCc.length > 0 && <Row label="Копія" value={finalCc.join(", ")} />}
                <Row label="Тема" value={packet.emailSubject} />
                <Row
                  label="Мова листа"
                  value={needsTranslation ? "Англійська (перекладено AI, перевірено)" : "Українська"}
                />
                <Row label="Вкладень" value={String([...selectedIds].length)} />
              </div>
              <p className="flex items-start gap-1.5 rounded-md border border-warning-foreground/20 bg-warning px-3 py-2 text-[13px] text-warning-foreground">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                Лист буде надіслано постачальнику. «Надіслано» означає, що його прийняв поштовий relay — не гарантує
                доставку/прочитання.
              </p>
            </div>
          )}

          {error && (
            <p role="alert" className="flex items-center gap-1.5 text-xs text-destructive">
              <AlertCircle className="h-3.5 w-3.5" /> {error}
            </p>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 border-t px-4 py-3">
          {step === "review" ? (
            <Button variant="ghost" size="sm" onClick={() => setStep("prepare")} disabled={mut.isPending}>
              <ChevronLeft className="mr-1 h-4 w-4" aria-hidden /> Назад
            </Button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onClose} disabled={mut.isPending}>
              Скасувати
            </Button>
            {step === "prepare" ? (
              <Button size="sm" onClick={() => setStep("review")} disabled={!canProceed}>
                Переглянути <Send className="ml-1 h-4 w-4" aria-hidden />
              </Button>
            ) : (
              <Button size="sm" onClick={submit} disabled={mut.isPending || !canProceed} className="gap-1.5">
                {mut.isPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Send className="h-4 w-4" aria-hidden />}
                Надіслати
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3 py-0.5">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{value}</span>
    </div>
  );
}
